import { Component, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { ImportStrategyReference, ImportType } from "@app/main/modules/foundation/foundation.enums";
import { ICsvImportCompletionResultDTO } from "@app/main/modules/foundation/foundation.interfaces";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize, tap } from "rxjs/operators";
import './peripheral-list.component.scss';

interface IProductVendor {
    name: string;
    value: number;
}

@UntilDestroy()
@Component({
    selector: 'peripheral-list',
    template: require('./peripheral-list.component.html')
})
export class PeripheralListComponent implements OnInit {
    peripherals: IPeripheral[];

    tableColumns: IVTableColumn<IPeripheral>[];
    tableRows: IVTableRow[];

    showDeviceImport: boolean;

    importStrategyRef = ImportStrategyReference.Device;
    importType = ImportType.Peripheral;
    loading: boolean;

    ProductVendor = ProductVendor;
    productVendors: IProductVendor[];

    filterTabs: IFilterBarTab<IPeripheral>[] = [];

    camerasView: boolean;

    importButtonString: string;

    constructor(
        private readonly accountFeatureService: AccountFeatureService,
        private readonly filterBarService: FilterBarService,
        private readonly navigationService: NavigationService,
        private readonly peripheralService: PeripheralService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.setVendors();
        this.loadData();
    }

    onRowClicked(row: IVTableRow): void {
        const peripheral: IPeripheral = row.data;
        const viewName: string = this.camerasView ? `${FoundationNavigationModule.Cameras}.view` : `${FoundationNavigationModule.Peripherals}.view`;
        this.navigationService.go(viewName, {serial: peripheral.Serial })
    }

    addDevices(): void {
        this.showDeviceImport = true;
    }

    closeShowDeviceForm(): void {
        this.showDeviceImport = false;
    }

    filterPeripheralByVendor(peripheral: IPeripheral, vendor: IProductVendor): boolean {
        return peripheral.Product.GroupId === vendor.value;
    }

    vendorSearchPredicate(vendor: any, searchText: string): boolean {
        return vendor.name.toLowerCase().indexOf(searchText) >= 0;
    }

    onCsvModelsSaved(event: ICsvImportCompletionResultDTO) {
        this.loading = true;

        const newPeripherals = JSON.parse(event.CreatedModels) as IPeripheral[];
        this.peripherals.push(...newPeripherals);

        this.tableRows = this.vTableService.createTableRows(this.peripherals, this.tableColumns);

        this.loading = false;
    }

    //--

    private loadData(): void {
        this.importButtonString = this.translationService.t("FoundationPeripherals.ImportPlaceholder");

        this.createTable();
        this.createTabs();
        this.loading = true;

        this.peripheralService.getAllPeripheralsWithMappings().pipe(
            untilDestroyed(this)
        ).subscribe({
            next: peripherals => {
                this.camerasView = this.navigationService.getLocation().split("/").includes("cameras");

                this.setImportButtonText();

                this.peripherals = peripherals.filter(peripheral => {
                    // Catch for new Streamax products with no Features flag till we can update the database and add products in Foundation
                    if (peripheral.Product?.GroupId === ProductVendor.Streamax || peripheral.Product?.GroupId === ProductVendor.AAMP) {
                        return this.camerasView;
                    }

                    return this.accountFeatureService.featureFlagsHaveFeatureAccess(peripheral.Product?.Features, AccountFeatures.Camera) === this.camerasView;
                });

                this.tableRows = this.vTableService.createTableRows(peripherals, this.tableColumns);

                this.loading = false;
            }
        })
    }

    private setImportButtonText(): void {
        this.importButtonString = this.camerasView ?
            this.translationService.t("FoundationPeripherals.CameraImportButton") :
            this.translationService.t("FoundationPeripherals.DeviceImport");
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Installed", width: 150, minTableWidth: 400, sorted: true, sortDirection: SortDirection.Descending, sortType: SortType.Date }),
                createCellFunction: (peripheral) => this.vTableService.createDateCell(peripheral.vehicleMapping?.StartUtc)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (peripheral) => this.vTableService.createVehicleRegCell(peripheral.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial", width: 150
                }),
                createCellFunction: (peripheral) => this.vTableService.createTableCell(peripheral.Serial)
            },

            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Name", minTableWidth: 500 }),
                createCellFunction: (peripheral) => this.vTableService.createTableCell(peripheral.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Vendor", width: 150, minTableWidth: 450 }),
                createCellFunction: (peripheral) => this.vTableService.createTableCell(peripheral.productVendorString)
            }
        ]

    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheral>("Common.All", (peripheral) => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheral>("Common.Installed", (peripheral) => !!peripheral.vehicleMapping?.StartUtc));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheral>("Common.NotInstalled", (peripheral) => !peripheral.vehicleMapping?.StartUtc));
    }

    private setVendors(): void {
        const vendorsList = Object.keys(ProductVendor).filter(s => !isNaN(Number(s)));
        this.productVendors = vendorsList.map(enumKey => {
            return {
                name: ProductVendor[enumKey],
                value: Number(enumKey)
            }
        });

        this.productVendors.sort((a, b) => {
            return a.name > b.name ? 1 : -1;
        })
    }
}
